// The run: what you're doing on each island and how you get to the next one.
// Replaces the first build's endless wave loop with the Godot game's shape
// (GAMEPLAY_LOOP.md §4.8, §5; scripts/autoload/run_manager.gd, extraction_sequence.gd):
//
//   a VILLAGE is three islands:
//     1. combat island   breather → 3 waves → the saloon doors light up
//     2. combat island   breather → 4 waves (Metal Brute anchors the last) → doors
//     3. boss island     breather → the village's BOSS → the blood moon rises: EXTRACTION
//                        (sim/bosses/index.ts ROTATION: Cerberus, the Bulwark Cube, the Black Court,
//                        the Angler, cycling; a boss not ported yet passes its village to the next)
//   EXTRACTION (the getaway, not a victory lap): the screen drains to near-greyscale, a brass
//     bell floats over the islet off the boss island's rim. Fly over and shoot it to call the
//     train. Hold out 30 s while the train comes down (a Metal Brute crashes the party at 5 s),
//     then jump aboard. Once one cowboy is on, the rest get a 12 s last call.
//     THE LEVIATHAN (BOSS_GRAMMAR §4b, sim/bosses/leviathan.ts) rises beside the islet the moment the
//     bell rings, every extraction, and harries the hold; its wound carries across the whole run.
//   then the next village (harder), or the saloon thread takes the posse home (onRunEnd).
//
//   the BONUS ISLAND (James, 2026-10-21): when island 2 is cleared a second set of saloon doors
//     lights up red on a sky islet (fly up to it). Take it and the posse warps to THE GAUNTLET:
//     three elite waves (two tiers tougher, coming fast) against a 150 s clock, no lives back on
//     the clear. Beat the clock and the prize vault opens: two power-ups per cowboy and a second
//     power-up slot for the rest of the run. Run out of time and G.U.L.P. pulls the plug: no
//     prize, and whatever lives you spent stay spent. Either way the doors then lead to the boss.
//     Skip it and you walk into the boss fresh. Only worth it if the posse feels strong.
//
// Islands are swapped, not streamed: walking through the doors starts a 2.5 s posse warp,
// after which every machine rebuilds the World from (run seed, island index). Geometry never
// crosses the wire. Everyone warps together; downed or out-of-lives cowboys get back up on
// the far side (a fresh island is a fresh start).

import { Rng, hash32, len2, secs } from './core';
import { HELI_SHIELD, WARDED_ELIGIBLE, MUT_COUNT, MUT_FLAVOR, MUT_NAME, MUT_OVERBUILT_HULL, Mut, AEGIS_SHIELD, AFFIX_CHANCE, AFFIX_ELIGIBLE, AFFIX_HP_MUL, AFFIX_MAX_PER_ISLAND, AFFIX_NAME, Affix, CAPTAIN_HP, REDSHELL_ELIGIBLE, VAULTLOCK_ELIGIBLE, ENEMIES, EType } from './defs';
import { Ev } from './codec';
import { WHISKEY_MAX, WHISKEY_PER_ISLAND, WHISKEY_START } from './whiskey';
import { Phase, isTargetable, respawnAt } from './player';
import { BONUS_INDEX, ISLANDS_PER_VILLAGE, IslandKind, World, GATE, NO_GROUND } from './world';
import { spawnCritters } from './critters';
import { BossKind } from './bosses/types';
import { PURSUER, bossDef, bossForVillage } from './bosses/index';
import { pursuerCap, resetLeviathan, summonLeviathan } from './bosses/leviathan';
import { PU } from './powerups';
import { contractEffect } from './progression';
import { DraftKind, clearHand } from './cards';
import type { Enemy } from './enemies';
import { setSilent } from './enemies';
import { Elem } from './weapons';
import { ELEMS, clearElems, gunElem, gunLevel } from './elements';
import { RULES, modsOf } from './player';
import type { Game } from './game';

export const enum RunPhase {
  Idle = 0, Breather = 1, Waves = 2, GateOpen = 3, Warping = 4, BossIntro = 5, Boss = 6,
  ExtractBell = 7, ExtractHoldout = 8, ExtractBoard = 9, Ended = 10,
  /** the chase (CHASE.md): back across the village with THE LEVIATHAN behind you, island by island, to the bell */
  ExtractTraverse = 11,
}
/** the chase: the Leviathan's island-breaking SWING is telegraphed this long once the first cowboy takes the doors; camp a
 *  traversal island this long and it swings anyway (a warning first); a cowboy who misses the doors goes down with the island */
export const SWING_SECS = 7, CAMP_WARN_SECS = 50, CAMP_BREAK_SECS = 65, GATE_SAFE_R = 14, DOWN_WITH_ISLAND_HP = 30;
/** gold bounty robots (extraction_sequence golden tickets): every third pressure pulse leads with one; three tickets pay a draft */
export const GOLD_HP_MUL = 1.5, TICKETS_PER_DRAFT = 3;
export const enum RunResult { None = 0, Extracted = 1, Wiped = 2 }

/** calm before the first wave on each island (the opening one is longest: you just landed) */
export const BREATHER_SECS = [20, 12, 10];
/** robots never spawn closer than this to a live cowboy (m) */
const SPAWN_MIN_DIST = 24;
export const WAVES_PER_ISLAND = [3, 4, 0];
export const WARP_SECS = 2.5;
export const HOLDOUT_SECS = 30;
export const LAST_CALL_SECS = 12;
/** the bonus gauntlet: waves, the clock, the breather before it */
export const BONUS_WAVES = 3, BONUS_CLOCK_SECS = 150, BONUS_BREATHER_SECS = 8;
/** power-ups the prize vault pays per cowboy */
export const BONUS_PRIZES = 2;
export const END_CARD_SECS = 7;
/** the bell floats this high over the islet deck; you ring it by shooting it */
export const BELL_LIFT = 3.4, BELL_R = 1.25;
/** the train's open car door, out past the islet rim: get within this of it to board */
export const BOARD_R = 6.5;

export interface RunState {
  phase: RunPhase;
  village: number; // 0-based
  island: number; // 0..2 within the village
  timer: number; // ticks left in the current phase (when it has one)
  wave: number;
  waves: number;
  queue: { at: number; type: EType; x: number; z: number; warned: boolean; nemesis?: boolean; affix?: number; anchor?: boolean; big?: boolean; el?: number; elLevel?: number; hpMul?: number; group?: number }[];
  /** the chase (CHASE.md): the camp clock on a traversal island, the swing telegraph (0 none), who already made the doors
   *  (bit per slot), golden tickets collected, pressure pulses so far (every third leads with a gold robot) */
  campT: number; swingT: number; safeMask: number; tickets: number; pulses: number;
  /** the roster lane (ROSTER.md): affixes rolled on this island; the miniboss group on the island (kind, its living kids, phase) */
  elites: number;
  mini: { kind: number; ids: number[]; phase: number; x: number; z: number } | null;
  holdChaffUntil: number;
  warpBy: number;
  bellBy: number;
  boardMask: number; // bit per pid aboard the train
  lastCall: number; // ticks; 0 = nobody aboard yet
  pressureT: number;
  bruteSent: boolean;
  result: RunResult;
  prevX: Map<number, number>;
  started: boolean;
  /** on the bonus island right now / taken it already this village */
  bonus: boolean;
  bonusUsed: boolean;
  /** the gauntlet clock (ticks), counting while its waves run */
  clock: number;
  /** where the doors lead: 0 onward, 1 the bonus island */
  warpTo: number;
  /** extra robot tiers from the signed contract (Hostile Takeover; sim/progression.ts) */
  tierBonus: number;
  /** the boss this island holds (BossKind.None off the boss island); sent so clients preload it */
  bossKind: BossKind;
}

export function newRun(): RunState {
  return {
    phase: RunPhase.Idle, village: 0, island: 0, timer: 0, wave: 0, waves: 0, queue: [], holdChaffUntil: 0,
    warpBy: -1, bellBy: -1, boardMask: 0, lastCall: 0, pressureT: 0, bruteSent: false,
    result: RunResult.None, prevX: new Map(), started: false, bonus: false, bonusUsed: false, clock: 0, warpTo: 0, tierBonus: 0, elites: 0, mini: null, campT: 0, swingT: 0, safeMask: 0, tickets: 0, pulses: 0,
    bossKind: BossKind.None,
  };
}

/** Difficulty tier: robots get tougher each village and a notch on the second island. */
export const runTier = (r: RunState) => r.village * 2 + (r.island >= 1 ? 1 : 0) + (r.bonus ? 2 : 0) + r.tierBonus;
export const islandIndex = (r: RunState) => (r.bonus ? BONUS_INDEX + r.village : r.village * ISLANDS_PER_VILLAGE + r.island);

const ISLAND_NAMES = [
  ['DUSTBOWL OUTSKIRTS', 'MAIN STREET', 'THE JUNKYARD'],
  ['TUMBLEWEED FLATS', 'BOOMTOWN', 'THE SCRAPHEAP'],
  ['BUZZARD MESA', 'COPPER GULCH', 'THE KENNEL'],
];
export function islandName(village: number, island: number, bonus = false) {
  if (bonus) return ['THE GAUNTLET', 'THE PROVING GROUNDS', 'THE MEAT GRINDER'][village % 3];
  return ISLAND_NAMES[village % ISLAND_NAMES.length][island];
}

/** Bell position over the extraction islet. */
export function bellPos(w: World) {
  const isl = w.extractIslet;
  let gy = w.terrain(isl.cx, isl.cz);
  if (gy === NO_GROUND) gy = isl.baseY;
  return { x: isl.cx, y: gy + BELL_LIFT, z: isl.cz };
}
/** The train's open door, hanging off the far side of the islet. */
export function boardPos(w: World) {
  const isl = w.extractIslet;
  const l = len2(isl.cx, isl.cz) || 1;
  const ux = isl.cx / l, uz = isl.cz / l;
  let gy = w.terrain(isl.cx, isl.cz);
  if (gy === NO_GROUND) gy = isl.baseY;
  return { x: isl.cx + ux * (isl.radius * 0.8 + 6), y: gy + 1.2, z: isl.cz + uz * (isl.radius * 0.8 + 6), ux, uz };
}

// ------------------------------------------------------------------ lifecycle
/** Start a fresh run at village 1, island 1 (or a later island, for testing), or deeper under a signed
 *  contract (g.contract, sim/progression.ts). Nothing in the run ticks before this. */
export function startRun(g: Game, island = 0, bonus = false) {
  const r = g.run;
  const k = contractEffect(g.contract);
  r.village = k.village;
  r.tierBonus = k.tiers;
  r.started = true;
  r.bonusUsed = false;
  g.powerups.reset();
  for (const p of g.players.values()) { p.pu = p.pu2 = PU.None; p.puEl = p.pu2El = 0; p.puCap = 1; p.bottles = WHISKEY_START; clearHand(p); clearElems(p); p.wl0 = p.wl1 = 0; p.nadeLevel = 1; p.killsNoHit = 0; p.hpLost = 0; g.sharedWatch = 0; p.phoenixUsed = 0; p.eagleUsed = 0; p.horseshoeUsed = 0; p.shotN = 0; }
  g.lives = g.posseLives();
  g.nemesis.reset();
  resetLeviathan(g);
  rollMutators(g);
  g.emit(Ev.Lives, [g.lives]);
  enterIsland(g, k.village, Math.max(0, Math.min(ISLANDS_PER_VILLAGE - 1, island | 0)), bonus, true);
}

function enterIsland(g: Game, village: number, island: number, bonus = false, first = false) {
  const r = g.run;
  if (village !== r.village) r.bonusUsed = false;
  r.village = village; r.island = island; r.bonus = bonus;
  if (bonus) r.bonusUsed = true;
  r.clock = bonus ? secs(BONUS_CLOCK_SECS) : 0; r.warpTo = 0;
  g.powerups.reset(); // drops nobody grabbed stay behind with the island
  const index = islandIndex(r);
  g.setWorld(new World(g.seed, index));
  r.bossKind = g.world.kind === IslandKind.Boss ? (g.devBoss || bossForVillage(village)) : BossKind.None;
  g.enemies = [];
  g.bullets.clear();
  for (const c of g.gas) c.on = false;
  g.arsenal.reset(); // grenades in flight, turrets and crates belong to the island
  g.boss = null;
  r.queue = []; r.wave = 0; r.waves = bonus ? BONUS_WAVES : WAVES_PER_ISLAND[island]; r.holdChaffUntil = 0;
  r.warpBy = -1; r.bellBy = -1; r.boardMask = 0; r.lastCall = 0; r.bruteSent = false; r.result = RunResult.None; r.elites = 0; r.mini = null;
  r.prevX.clear();
  for (const p of g.players.values()) {
    const wasPaused = p.paused;
    p.horseshoeUsed = 0; p.skyLandUsed = 0; // once-an-island cards re-arm
    respawnAt(p, g.world, g.world.spawnPoint(p.slot));
    if (wasPaused) { p.paused = 1; p.skyT = 1; }
    // Godot's stim economy: every new island puts another bottle of whiskey in the belt (sim/whiskey.ts)
    if (!first) p.bottles = Math.min(WHISKEY_MAX, p.bottles + WHISKEY_PER_ISLAND);
    p.drinkT = 0; p.stimT = 0;
  }
  g.critters = spawnCritters(g.world, island);
  g.placeBarrels(index); // elemental barrels by the buildings (ROSTER.md DISMANTLE)
  // the hexed strongbox: 35 % of combat islands (never the boss's, never the bonus), placed from the seed
  if (!bonus && g.world.kind !== IslandKind.Boss && hash32(g.seed, index, 0x4e3d) % 100 < 35) {
    const sp = g.world.spawnPoint(0), a = (hash32(g.seed, index, 0x4e3e) % 628) / 100, d = 6 + (hash32(g.seed, index, 0x4e3f) % 40) / 10;
    g.placeStrongbox(sp.x + Math.cos(a) * d, sp.z + Math.sin(a) * d);
  }
  // a munitions cache on half the combat islands: hold Interact to level the grenade's element (cap 3), or refill the belt
  if (!bonus && g.world.kind !== IslandKind.Boss && hash32(g.seed, index, 0xca5e) % 100 < 50) {
    const sp = g.world.spawnPoint(0), a = (hash32(g.seed, index, 0xca5f) % 628) / 100, d = 9 + (hash32(g.seed, index, 0xca60) % 50) / 10;
    g.placeCache(sp.x + Math.cos(a) * d, sp.z + Math.sin(a) * d);
  }
  // Posse Repair Kit: each new village grants the posse a shared feather per kit held
  if (island === 0 && !bonus && !first) for (const p of g.players.values()) if (modsOf(p).repairKit) { g.sharedWatch++; g.emit(Ev.Notice, [], `THE POSSE REPAIR KIT · A SHARED FEATHER (${g.sharedWatch})`); }
  setPhase(g, RunPhase.Breather, secs(bonus ? BONUS_BREATHER_SECS : BREATHER_SECS[island]));
  g.emit(Ev.Island, [index, g.world.kind, village, island], islandName(village, island, bonus));
  const kind = bonus ? `BONUS ROUND · ${BONUS_WAVES} ELITE WAVES IN ${BONUS_CLOCK_SECS} S` : g.world.kind === IslandKind.Boss ? 'BOSS ISLAND' : `ISLAND ${island + 1} OF ${ISLANDS_PER_VILLAGE}`;
  g.emit(Ev.Notice, [], `${islandName(village, island, bonus)} · ${kind}`);
  if (first && g.muts) g.emit(Ev.Notice, [], `RUN CONDITIONS: ${mutatorNames(g).join(' · ')} — ${MUT_FLAVOR.filter((_, i) => g.mut(i as Mut)).join('; ')}`); // the run-start stamp
}

function setPhase(g: Game, phase: RunPhase, ticks: number) {
  g.run.phase = phase;
  g.run.timer = ticks;
  g.emit(Ev.Run, [phase, ticks, g.run.wave, g.run.waves]);
}

/** Every player down with no lives left. */
export function wipeRun(g: Game) {
  const r = g.run;
  if (r.phase === RunPhase.Ended) return;
  endRun(g, RunResult.Wiped);
  g.emit(Ev.Notice, [], 'POSSE WIPED. G.U.L.P. IS DOCKING YOUR PAY.');
}

function endRun(g: Game, result: RunResult) {
  const r = g.run;
  r.result = result;
  setPhase(g, RunPhase.Ended, secs(END_CARD_SECS));
  g.emit(Ev.RunEnd, [result === RunResult.Extracted ? 0 : 1, r.village, r.island]);
}

// ------------------------------------------------------------------ the tick
export function stepRun(g: Game) {
  const r = g.run;
  if (!r.started || r.phase === RunPhase.Idle) return;
  const anyone = [...g.players.values()].some((p) => p.phase === Phase.Alive && !p.paused);
  if (r.timer > 0 && (anyone || r.phase === RunPhase.Ended || r.phase === RunPhase.Warping)) r.timer--;

  switch (r.phase) {
    case RunPhase.Breather:
      if (r.timer <= 0) {
        if (g.world.kind === IslandKind.Boss) spawnBoss(g);
        else nextWave(g);
      }
      break;
    case RunPhase.Waves:
      if (!anyone) break;
      if (r.bonus && r.clock > 0 && --r.clock === 0) { bonusTimeUp(g); break; }
      if (r.timer > 0) break; // intermission between waves
      releaseQueue(g);
      stepMiniboss(g);
      // the gauntlet doesn't wait for stragglers: the next wave comes in with three still standing
      const left = livingRobots(g);
      if (r.queue.length === 0 && (left === 0 || (r.bonus && r.wave < r.waves && left <= 3))) {
        if (r.wave >= r.waves) islandCleared(g);
        else { r.timer = secs(r.bonus ? 2 : 5); g.arsenal.onWaveEnd(r.wave, false); scheduleNext(g); }
      }
      break;
    case RunPhase.GateOpen:
      watchGate(g);
      break;
    case RunPhase.Warping:
      if (r.timer <= 0) {
        if (r.warpTo === 2) enterTraverseIsland(g, r.island - 1);
        else if (r.warpTo === 1) enterIsland(g, r.village, r.island, true);
        else if (r.island + 1 < ISLANDS_PER_VILLAGE) enterIsland(g, r.village, r.island + 1);
      }
      break;
    case RunPhase.BossIntro:
    case RunPhase.Boss:
      if (r.phase === RunPhase.BossIntro && r.timer <= 0) setPhase(g, RunPhase.Boss, 0);
      break;
    case RunPhase.ExtractTraverse:
      pressure(g, false);
      stepTraverse(g);
      break;
    case RunPhase.ExtractBell:
      pressure(g, false);
      break;
    case RunPhase.ExtractHoldout:
      pressure(g, true);
      if (!r.bruteSent && r.timer <= secs(HOLDOUT_SECS - 5)) {
        r.bruteSent = true;
        const isl = g.world.extractIslet;
        const a = hash32(g.seed, g.tick) / 4294967296 * Math.PI * 2;
        g.spawnEnemy(EType.MetalBrute, isl.cx + Math.cos(a) * isl.radius * 0.4, isl.cz + Math.sin(a) * isl.radius * 0.4);
        g.emit(Ev.Notice, [], 'METAL BRUTE ON THE PLATFORM. HOLD THE LINE.');
      }
      if (r.timer <= 0) {
        setPhase(g, RunPhase.ExtractBoard, 0);
        g.emit(Ev.Notice, [], 'THE TRAIN IS IN. JUMP ABOARD.');
      }
      break;
    case RunPhase.ExtractBoard:
      pressure(g, true);
      board(g);
      break;
    case RunPhase.Ended:
      if (r.timer <= 0) finishEnded(g);
      break;
  }
}

function finishEnded(g: Game) {
  const r = g.run;
  const result = r.result;
  if (g.onRunEnd) {
    // the saloon owns what happens next (it calls startRun again)
    r.phase = RunPhase.Idle; r.started = false;
    // off the train: a boarded cowboy is held like a pause (isAboard), and in the saloon that froze him
    r.boardMask = 0; r.bellBy = -1; r.warpBy = -1;
    g.enemies = []; g.bullets.clear(); g.boss = null;
    g.onRunEnd(result === RunResult.Extracted ? 'extract' : 'wipe');
    return;
  }
  // no saloon wired: ride on into the next village, or start over after a wipe
  if (result === RunResult.Extracted) {
    g.lives = Math.min(g.posseLives(), g.lives + 2);
    g.emit(Ev.Lives, [g.lives]);
    enterIsland(g, r.village + 1, 0);
  } else {
    g.lives = g.posseLives();
    g.nemesis.reset();
    g.emit(Ev.Lives, [g.lives]);
    enterIsland(g, 0, 0);
    g.onWipe?.();
  }
}

// ------------------------------------------------------------------ waves
function livingRobots(g: Game) {
  let n = 0;
  for (const e of g.enemies) if (!e.dead) n++;
  return n;
}

function nextWave(g: Game) {
  const r = g.run;
  r.wave++;
  composeWave(g);
  setPhase(g, RunPhase.Waves, 0);
}

/** Between waves: a short intermission, then the next one composes and starts warning. */
/** Spawn points at least SPAWN_MIN_DIST from every live cowboy, so nobody gets robots dropped on
 *  their head (falls back to the three farthest on a small island). */
export function clearSpawns(g: Game) {
  const live = [...g.players.values()].filter((p) => p.phase === Phase.Alive);
  const clear = (sp: { x: number; z: number }) => live.reduce((m, p) => Math.min(m, len2(sp.x - p.x, sp.z - p.z)), Infinity);
  const pts = g.world.enemySpawns.filter((sp) => clear(sp) >= SPAWN_MIN_DIST * SPAWN_MIN_DIST);
  return pts.length >= 3 ? pts : [...g.world.enemySpawns].sort((a, b) => clear(b) - clear(a)).slice(0, 3);
}

function scheduleNext(g: Game) {
  const r = g.run;
  r.wave++;
  composeWave(g, secs(r.bonus ? 2 : 5));
  g.emit(Ev.Run, [RunPhase.Waves, r.timer, r.wave, r.waves]);
}

export function composeWave(g: Game, delay = 0) {
  const r = g.run;
  r.queue = [];
  r.holdChaffUntil = 0;
  // posse scaling: only cowboys actually in the fight count (a paused or dropped one is up in the sky)
  const n = g.activePosse();
  const scale = 0.55 + 0.45 * n;
  // the second island starts where the first one left off; the gauntlet starts past both
  const dw = r.wave + (r.bonus ? 4 : r.island === 1 ? 2 : 0);
  const tier = r.village;
  let budget = Math.round((5 + dw * 3.2 + tier * 6) * scale * (r.bonus ? 1.15 : 1));
  const rng = new Rng(hash32(g.seed, islandIndex(r), r.wave, 0x5afe));
  const types: EType[] = [];
  const finale = r.wave === r.waves;
  if (finale && r.island === 1) { types.push(EType.MetalBrute); budget = Math.round(budget * 0.5); }
  if (finale && r.bonus) types.push(EType.MetalBrute); // the gauntlet's last wave brings two
  const pool: EType[] = g.mut(Mut.Overbuilt) ? [EType.Swarmer, EType.Swarmer, EType.Shooter, EType.Shooter] : [EType.Swarmer, EType.Swarmer, EType.Swarmer, EType.Shooter, EType.Shooter]; // OVERBUILT: a third less chaff
  if (dw >= 2) pool.push(EType.Bomber);
  if (dw >= 3) pool.push(EType.Sniper, EType.Brute, EType.Stalker); // the Stalker (ROSTER.md) once the fight has shape
  if (dw >= 4 || tier > 0) pool.push(EType.Brute, EType.Shooter, EType.Bomber, EType.Stalker);
  if (tier >= 1 && dw >= 3) pool.push(EType.FlyingShooter); // the fliers (ROSTER.md FLYERS): look up
  if (tier >= 2 && dw >= 3) pool.push(EType.FlyingSniper);
  // exactly one support unit once the anchors arrive: a "kill the healer first" decision
  if (dw >= 3 || (tier > 0 && dw >= 2)) {
    const sup = rng.chance(0.5) ? EType.Disruptor : EType.Engineer;
    types.push(sup);
    budget -= ENEMIES[sup].cost;
  }
  while (budget > 0) {
    const t = rng.pick(pool);
    const cost = ENEMIES[t].cost;
    if (cost > budget && budget > 1) continue;
    types.push(t);
    budget -= cost;
  }
  if (types.some((t) => t === EType.Brute || t === EType.MetalBrute) && !g.mut(Mut.Zealous)) r.holdChaffUntil = g.tick + delay + secs(8); // ZEALOUS: no hesitation
  // group spawns from the points nearest the posse so fights have a front
  const center = posseCenter(g);
  const spawns = clearSpawns(g).sort((a, b) => len2(a.x - center.x, a.z - center.z) - len2(b.x - center.x, b.z - center.z)).slice(0, 7);
  let at = g.tick + delay + secs(1.5);
  for (let i = 0; i < types.length; i++) {
    const sp = spawns[rng.int(0, spawns.length - 1)];
    const a = rng.range(0, Math.PI * 2), rr = rng.range(0, 3.5);
    const t = types[i];
    // elite affixes (ROSTER.md): by village, at most AFFIX_MAX_PER_ISLAND an island, on mid-tier kits only
    const affix = rollAffix(r, t, rng);
    r.queue.push({ at, type: t, x: sp.x + Math.cos(a) * rr, z: sp.z + Math.sin(a) * rr, warned: false, affix, anchor: t === EType.Brute || t === EType.MetalBrute || affix !== Affix.None });
    if (affix !== Affix.None) g.emit(Ev.Notice, [], `${AFFIX_NAME[affix]} ${ENEMIES[t].name.toUpperCase()} INBOUND · ${AFFIX_HINT[affix]}`);
    if (i % 3 === 2) at += secs(rng.range(1.5, 3.2));
  }
  // a miniboss (ROSTER.md): the second island's middle wave from the second village on, one of the orchestrator squads
  if (!r.bonus && r.island === 1 && r.wave === r.waves - 1 && tier >= 1 && !r.mini) queueMiniboss(g, rng, spawns, at + secs(2));
  // a grudge from last wave comes back as a named outlaw (sim/nemesis.ts)
  g.nemesis.onWaveStart(g);
  if (finale && r.island === 1) g.emit(Ev.Notice, [], 'METAL BRUTE INBOUND. HYPER SHIELD: PRIME IT, THEN DETONATE.');
}

// ---------------------------------------------------------------- run mutators (MUTATORS.md, Godot run_mutators.gd)
/** one or two a run from the seed (Godot: seed ^ 0x5EED01, a shuffle, take 1-2); the banner lands with the first island */
export function rollMutators(g: Game) {
  const rng = new Rng(hash32(g.seed, 0x5eed01, 0));
  const pool = Array.from({ length: MUT_COUNT }, (_, i) => i);
  for (let i = pool.length - 1; i > 0; i--) { const j = rng.int(0, i); const t = pool[i]; pool[i] = pool[j]; pool[j] = t; }
  const n = rng.int(1, 2);
  g.muts = 0;
  for (let i = 0; i < n; i++) g.muts |= 1 << pool[i];
  setSilent(g.mut(Mut.Silent) === 1);
}
export function mutatorNames(g: Game) { return MUT_NAME.filter((_, i) => g.mut(i as Mut)); }
/** MIRROR: the element the posse has installed most (sim/elements.ts), or none */
export function favouriteElement(g: Game) {
  const n = new Map<number, number>();
  for (const p of g.players.values()) for (const s of [0, 1]) { const el = gunElem(p, s); if (el !== Elem.None) n.set(el, (n.get(el) ?? 0) + gunLevel(p, s)); }
  let best = Elem.None, bn = 0;
  for (const [el, k] of n) if (k > bn) { bn = k; best = el; }
  return best;
}

// ---------------------------------------------------------------- the roster lane (ROSTER.md): affixes and minibosses
const AFFIX_HINT = ['', 'AV4 PLATING: ACID, FIRE OR A DETONATION', 'A SHIELD THAT COMES BACK: KEEP HITTING IT', 'HYPER SHIELD: PRIME IT, THEN DETONATE', 'A SHELL ONLY MELEE BREAKS: GET IN CLOSE', 'A HELICOPTER SHIELDS IT: SHOOT THE HELICOPTER FIRST'];
function rollAffix(r: RunState, t: EType, rng: Rng): Affix {
  if (r.elites >= AFFIX_MAX_PER_ISLAND || !AFFIX_ELIGIBLE.includes(t)) return Affix.None;
  if (rng.next() >= AFFIX_CHANCE[Math.min(AFFIX_CHANCE.length - 1, r.village)]) return Affix.None;
  const pool: Affix[] = [Affix.Ironclad, Affix.Aegis];
  if (VAULTLOCK_ELIGIBLE.includes(t)) pool.push(Affix.Vaultlock);
  if (REDSHELL_ELIGIBLE.includes(t)) pool.push(Affix.Redshell);
  if (WARDED_ELIGIBLE.includes(t) && r.village >= 1) pool.push(Affix.Warded);
  r.elites++;
  return rng.pick(pool);
}
export function applyAffix(e: Enemy, affix: Affix) {
  e.affix = affix; e.anchor = 1;
  e.hp *= AFFIX_HP_MUL; e.maxHp *= AFFIX_HP_MUL;
  if (affix === Affix.Ironclad) e.armor = 4;
  else if (affix === Affix.Aegis) { e.maxShield = Math.max(e.maxShield, AEGIS_SHIELD); e.shield = e.maxShield; }
  else if (affix === Affix.Vaultlock) e.hyper = 1;
  else if (affix === Affix.Redshell) e.redshell = 1;
  // Warded: its helicopter is paired at the spawn (releaseQueue), not here
}
/** the orchestrator minibosses (Godot brute_brothers / chroma_squad / disruptor_choir / swarm_captain): squads of the
 *  robots we have, with a hook. 0 Brute Brothers: two 180-hull Brutes on opposite sides. 1 Chroma Squad: five Shooters,
 *  one of each element, then five elemental Brutes at L3 where they fell. 2 Disruptor Choir: three Disruptors around
 *  an Aegis Shooter. 3 Swarm Captain: a big Swarmer that births a brood. Down → a card draft (the miniboss card). */
export const enum Mini { Brothers = 0, Chroma = 1, Choir = 2, Captain = 3, Squadron = 4 }
export const MINI_NAME = ['THE BRUTE BROTHERS', 'THE CHROMA SQUAD', 'THE DISRUPTOR CHOIR', 'THE SWARM CAPTAIN', 'THE SKY SQUADRON'];
function queueMiniboss(g: Game, rng: Rng, spawns: { x: number; z: number }[], at: number) {
  const r = g.run;
  const kind = rng.int(0, 4) as Mini;
  const sp = spawns[rng.int(0, spawns.length - 1)];
  r.mini = { kind, ids: [], phase: 0, x: sp.x, z: sp.z };
  const q = (type: EType, x: number, z: number, o: Partial<RunState['queue'][number]> = {}) => r.queue.push({ at, type, x, z, warned: false, group: kind, anchor: true, ...o });
  if (kind === Mini.Brothers) { q(EType.Brute, sp.x + 8, sp.z, { hpMul: 180 / 140 }); q(EType.Brute, sp.x - 8, sp.z, { hpMul: 180 / 140 }); }
  else if (kind === Mini.Chroma) ELEMS.forEach((el, i) => { const a = (i / 5) * Math.PI * 2; q(EType.Shooter, sp.x + Math.cos(a) * 6, sp.z + Math.sin(a) * 6, { el, elLevel: 1 }); });
  else if (kind === Mini.Choir) { q(EType.Shooter, sp.x, sp.z, { affix: Affix.Aegis }); for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; q(EType.Disruptor, sp.x + Math.cos(a) * 5, sp.z + Math.sin(a) * 5); } }
  else if (kind === Mini.Squadron) { q(EType.FlyingSniper, sp.x, sp.z + 8); q(EType.FlyingShooter, sp.x - 8 * 0.866, sp.z - 4); q(EType.FlyingShooter, sp.x + 8 * 0.866, sp.z - 4); } // sky_squadron.gd: a triangle, the sniper at the point
  else q(EType.Swarmer, sp.x, sp.z, { big: true });
  g.emit(Ev.Notice, [], `${MINI_NAME[kind]} · ${['TWO BRUTES, A PINCER', 'FIVE ELEMENTS: HIT EACH WITH WHAT BEATS IT', 'KILL THE SUPPORTS FIRST', 'KILL THE CAPTAIN: THE BROOD SCATTERS', 'LOOK UP: ROCKETS, THE BIG IRON, OR FLY TO THEM'][kind]}`);
}
function stepMiniboss(g: Game) {
  const r = g.run, m = r.mini;
  if (!m || m.phase >= 9) return;
  if (r.queue.some((o) => o.group !== undefined)) return; // still landing
  if (m.ids.some((id) => g.enemies.some((e) => e.id === id && !e.dead))) return;
  if (m.kind === Mini.Chroma && m.phase === 0) {
    // phase two: five elemental Brutes at L3 where the Shooters stood
    m.phase = 1; m.ids = [];
    ELEMS.forEach((el, i) => { const a = (i / 5) * Math.PI * 2; r.queue.push({ at: g.tick + secs(1), type: EType.Brute, x: m.x + Math.cos(a) * 6, z: m.z + Math.sin(a) * 6, warned: false, group: m.kind, anchor: true, el, elLevel: 3 }); });
    g.emit(Ev.Notice, [], 'THE CHROMA SQUAD · PHASE TWO: FIVE ELEMENTAL BRUTES');
    return;
  }
  m.phase = 9;
  g.emit(Ev.Notice, [], `${MINI_NAME[m.kind]} DOWN`);
  g.offerDrafts(DraftKind.Uncommon, 0x3155 + islandIndex(r)); // the miniboss card
}

function releaseQueue(g: Game) {
  const r = g.run;
  for (const o of r.queue) {
    if (!o.warned && g.tick >= o.at - secs(1.2)) {
      o.warned = true;
      g.emit(Ev.SpawnWarn, [o.x, g.world.support(o.x, o.z, 60), o.z, o.type, secs(1.2)]);
    }
  }
  const isChaff = (t: EType) => t === EType.Swarmer;
  for (let i = 0; i < r.queue.length; i++) {
    const o = r.queue[i];
    if (g.tick < o.at || (isChaff(o.type) && g.tick < r.holdChaffUntil)) continue;
    if (livingRobots(g) + 1 > 30) { o.at = g.tick + secs(1); continue; }
    const e = g.spawnEnemy(o.type, o.x, o.z);
    e.wave = r.wave; e.anchor = o.anchor ? 1 : 0;
    if (g.mut(Mut.Overbuilt) && (o.type === EType.Brute || o.type === EType.MetalBrute)) { e.hp *= MUT_OVERBUILT_HULL; e.maxHp *= MUT_OVERBUILT_HULL; }
    if (g.mut(Mut.Mirror) && e.armorEl !== Elem.None) { const fav = favouriteElement(g); if (fav !== Elem.None) e.armorEl = fav; }
    if (o.affix) applyAffix(e, o.affix);
    if (o.affix === Affix.Warded) { const heli = g.spawnEnemy(EType.Helicopter, e.x + 3, e.z + 3); heli.ward = e.id; heli.wave = e.wave; heli.y = e.y + 6; e.maxShield = Math.max(e.maxShield, HELI_SHIELD); e.shield = HELI_SHIELD; } // WARDED: the helicopter rides in with it
    if (o.big) { e.big = 1; e.hp = e.maxHp = CAPTAIN_HP * Math.pow(1.18, runTier(r)); e.anchor = 1; }
    if (o.hpMul) { e.hp *= o.hpMul; e.maxHp *= o.hpMul; }
    if (o.el !== undefined) { e.armorEl = o.el; e.elLevel = o.elLevel ?? 1; }
    if (o.group !== undefined && r.mini) r.mini.ids.push(e.id);
    if (o.nemesis) g.nemesis.promote(e, g);
    r.queue.splice(i--, 1);
  }
}

function islandCleared(g: Game) {
  const r = g.run;
  g.arsenal.onWaveEnd(r.wave, true);
  r.prevX.clear();
  const c = posseCenter(g), gy = posseGroundY(g);
  const rng = new Rng(hash32(g.seed, islandIndex(r), 0x9017));
  if (r.bonus) {
    // the prize vault: no lives back (that's the risk), but two power-ups each and a second slot
    const n = Math.max(1, g.players.size) * BONUS_PRIZES;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2, rr = 3.2 + (i % 2) * 1.4;
      g.powerups.drop(c.x + Math.cos(a) * rr, c.z + Math.sin(a) * rr, rng.next(), rng.next(), 0, gy, c.x, c.z);
    }
    for (const p of g.players.values()) p.puCap = 2;
    g.offerDrafts(DraftKind.Uncommon, 0xca4d + islandIndex(r)); // the gauntlet pays a draft too (Godot's cargo draft)
    setPhase(g, RunPhase.GateOpen, 0);
    g.emit(Ev.Notice, [], `GAUNTLET BEATEN WITH ${Math.ceil(r.clock / 60)} S TO SPARE. PRIZE VAULT OPEN: ${BONUS_PRIZES} POWER-UPS EACH, AND A SECOND SLOT.`);
    return;
  }
  g.lives = Math.min(g.posseLives(), g.lives + 2);
  g.emit(Ev.Lives, [g.lives]);
  // one power-up per cleared island (Godot dropped 1-2 a floor)
  g.powerups.drop(c.x + rng.range(-3, 3), c.z + rng.range(-3, 3), rng.next(), rng.next(), 0, gy, c.x, c.z);
  g.offerDrafts(DraftKind.Uncommon, 0xca4d + islandIndex(r)); // the bridge draft (sim/cards.ts)
  setPhase(g, RunPhase.GateOpen, 0);
  g.emit(Ev.Notice, [], g.world.bonusDoor && !r.bonusUsed
    ? 'ISLAND LIQUIDATED. SALOON DOORS ONWARD, OR FLY UP TO THE RED DOORS FOR THE BONUS ROUND.'
    : 'ISLAND LIQUIDATED. THE SALOON DOORS ARE OPEN.');
}

/** The gauntlet clock ran out: G.U.L.P. pulls the plug. No prize; the doors lead on to the boss. */
function bonusTimeUp(g: Game) {
  const r = g.run;
  for (const e of g.enemies) if (!e.dead) g.killEnemy(e, -1);
  r.queue = [];
  r.prevX.clear();
  setPhase(g, RunPhase.GateOpen, 0);
  g.emit(Ev.Notice, [], "TIME. G.U.L.P. PULLED THE PLUG: NO PRIZE. THE DOORS LEAD ON TO THE BOSS.");
}

export function posseCenter(g: Game) {
  let x = 0, z = 0, k = 0;
  for (const p of g.players.values()) if (isTargetable(p)) { x += p.x; z += p.z; k++; }
  return k ? { x: x / k, z: z / k } : { x: g.world.playerSpawn.x, z: g.world.playerSpawn.z };
}
/** the height the posse last stood at (power-ups land on that ground, not on a roof overhead) */
function posseGroundY(g: Game) {
  let y = 0, k = 0;
  for (const p of g.players.values()) if (isTargetable(p)) { y += p.lastGy; k++; }
  return k ? y / k : g.world.spawnPoint(0).y;
}

// ------------------------------------------------------------------ the saloon doors
/** Is the bonus door open right now? (cleared island 2, not taken yet this village) */
export const bonusDoorOpen = (g: Game) => g.run.phase === RunPhase.GateOpen && !!g.world.bonusDoor && !g.run.bonusUsed;

function watchGate(g: Game) {
  const r = g.run;
  const gt = g.world.gate, gy = g.world.gateY();
  const bd = bonusDoorOpen(g) ? g.world.bonusDoor : null, by = bd ? g.world.bonusDoorY() : 0;
  // a crossing of a doorway plane, either way, between the posts and under the beam
  const through = (px: number, x: number, y: number, z: number, dx: number, dz: number, dy: number) =>
    (px < dx) !== (x < dx) && Math.abs(z - dz) <= GATE.halfW + 0.2 && y >= dy - 1.5 && y <= dy + GATE.height + 0.4;
  for (const p of g.players.values()) {
    const px = r.prevX.get(p.id);
    r.prevX.set(p.id, p.x);
    if (px === undefined || p.phase !== Phase.Alive || p.paused || p.skyT > 0) continue;
    const bonus = !!bd && through(px, p.x, p.y, p.z, bd.x, bd.z, by);
    if (!bonus && !through(px, p.x, p.y, p.z, gt.x, gt.z, gy)) continue;
    r.warpBy = p.id;
    r.warpTo = bonus ? 1 : 0;
    setPhase(g, RunPhase.Warping, secs(WARP_SECS));
    g.emit(Ev.Notice, [], bonus
      ? `${p.name.toUpperCase()} TOOK THE RED DOORS. BONUS ROUND: HOLD ON TO YOUR HATS.`
      : `${p.name.toUpperCase()} PUSHED THROUGH THE DOORS. THE POSSE RIDES ON.`);
    return;
  }
}

// ------------------------------------------------------------------ boss + extraction
function spawnBoss(g: Game) {
  const r = g.run;
  const n = Math.max(1, g.players.size);
  const ar = g.world.arena;
  if (r.bossKind === PURSUER) {
    // EMG_BOSS=leviathan: no island boss, straight into the hold (the bell rung) with the pursuer
    r.phase = RunPhase.Boss;
    bossDefeated(g);
    ringBell(g, [...g.players.keys()][0] ?? 0);
    return;
  }
  const def = bossDef(r.bossKind || bossForVillage(r.village));
  const tierMul = Math.pow(1.18, runTier(r));
  // it lands facing the posse
  const c = posseCenter(g);
  g.boss = def.spawn(g, ar.x, ar.z, c.x, c.z, n, tierMul);
  setPhase(g, RunPhase.BossIntro, secs(3));
  g.emit(Ev.Notice, [], def.recall);
}

/** Called when the boss dies: the blood moon rises. */
export function bossDefeated(g: Game) {
  const r = g.run;
  if (r.phase !== RunPhase.Boss && r.phase !== RunPhase.BossIntro) return;
  g.boss = null;
  // the adds rout when the warden falls
  for (const e of g.enemies) if (!e.dead && e.type === EType.Swarmer) g.killEnemy(e, -1);
  g.lives = Math.min(g.posseLives(), g.lives + 2);
  g.emit(Ev.Lives, [g.lives]);
  g.offerDrafts(DraftKind.Rare, 0xb055 + islandIndex(r)); // BOSS FELLED: the Rare draft (sim/cards.ts)
  r.pressureT = secs(6);
  r.tickets = 0; r.pulses = 0;
  if (RULES.chase && r.island > 0) {
    // the chase (CHASE.md): back across the village to the bell on its first island, THE LEVIATHAN behind you
    r.campT = 0; r.swingT = 0; r.safeMask = 0; r.prevX.clear();
    setPhase(g, RunPhase.ExtractTraverse, 0);
    g.emit(Ev.Notice, [], `THE BLOOD MOON RISES. THE BELL IS BACK ON ${islandName(r.village, 0).toUpperCase()}: TAKE THE DOORS. IT IS COMING.`);
    summonLeviathan(g);
    return;
  }
  setPhase(g, RunPhase.ExtractBell, 0);
  g.emit(Ev.Notice, [], 'THE BLOOD MOON RISES. FLY TO THE BELL AND SHOOT IT TO CALL THE TRAIN.');
}

/** the chase: the doors, the camp clock, the swing */
function stepTraverse(g: Game) {
  const r = g.run;
  const gt = g.world.gate, gy = g.world.gateY();
  if (r.swingT > 0) {
    // the island goes when the telegraph runs out: whoever reached the doors rides; the rest go down with it
    r.timer = r.swingT;
    for (const p of g.players.values()) if (p.phase === Phase.Alive && !p.paused && len2(p.x - gt.x, p.z - gt.z) < GATE_SAFE_R) r.safeMask |= 1 << (p.slot & 31);
    if (--r.swingT === 0) {
      for (const p of g.players.values()) {
        if (p.paused || (r.safeMask >> (p.slot & 31)) & 1) continue;
        p.hp = DOWN_WITH_ISLAND_HP; p.shield = 0;
        if (g.lives > 0) { g.lives--; g.emit(Ev.Lives, [g.lives]); }
        g.emit(Ev.Notice, [], `${p.name.toUpperCase()} WENT DOWN WITH THE ISLAND`);
      }
      r.warpTo = 2; r.warpBy = -1;
      setPhase(g, RunPhase.Warping, secs(WARP_SECS));
      g.emit(Ev.Notice, [], 'THE LEVIATHAN BREAKS THE ISLAND');
    }
    return;
  }
  // the camp clock: it will not wait on you
  r.campT++;
  if (r.campT === secs(CAMP_WARN_SECS)) g.emit(Ev.Notice, [], 'IT IS DONE WAITING. MOVE, OR GO DOWN WITH THE ISLAND.');
  if (r.campT >= secs(CAMP_BREAK_SECS)) { startSwing(g, -1); return; }
  // the first cowboy through the doors starts the swing
  const through = (px: number, x: number, y: number, z: number) => (px < gt.x) !== (x < gt.x) && Math.abs(z - gt.z) <= GATE.halfW + 0.2 && y >= gy - 1.5 && y <= gy + GATE.height + 0.4;
  for (const p of g.players.values()) {
    const px = r.prevX.get(p.id);
    r.prevX.set(p.id, p.x);
    if (px === undefined || p.phase !== Phase.Alive || p.paused || p.skyT > 0) continue;
    if (through(px, p.x, p.y, p.z)) { startSwing(g, p.id); return; }
  }
}
function startSwing(g: Game, by: number) {
  const r = g.run;
  r.swingT = secs(SWING_SECS); r.safeMask = 0; r.warpBy = by;
  const p = by >= 0 ? g.players.get(by) : undefined;
  g.emit(Ev.Notice, [], `${p ? p.name.toUpperCase() + ' TOOK THE DOORS. ' : ''}THE LEVIATHAN WINDS UP: ${SWING_SECS} SECONDS TO THE DOORS OR DOWN WITH THE ISLAND.`);
}
/** a traversal island: the world swaps back one, the pursuer rises beside it, the bell waits on the first island */
function enterTraverseIsland(g: Game, island: number) {
  const r = g.run;
  const left = [...g.players.values()].filter((p) => !p.paused && !((r.safeMask >> (p.slot & 31)) & 1)).map((p) => p.id);
  enterIsland(g, r.village, Math.max(0, island));
  for (const id of left) { const p = g.players.get(id); if (p) { p.hp = Math.min(p.hp, DOWN_WITH_ISLAND_HP); p.shield = 0; } }
  r.campT = 0; r.swingT = 0; r.safeMask = 0; r.pressureT = secs(4); r.prevX.clear();
  if (r.island === 0) {
    setPhase(g, RunPhase.ExtractBell, 0);
    g.emit(Ev.Notice, [], 'THE BELL. FLY TO THE PLATFORM AND SHOOT IT TO CALL THE TRAIN.');
  } else {
    setPhase(g, RunPhase.ExtractTraverse, 0);
    g.emit(Ev.Notice, [], `KEEP MOVING: THE BELL IS ON ${islandName(r.village, 0).toUpperCase()}. IT FOLLOWS.`);
  }
  summonLeviathan(g);
}

/** A player shot the bell. */
export function ringBell(g: Game, pid: number) {
  const r = g.run;
  g.emit(Ev.Bell, [pid]);
  if (r.phase !== RunPhase.ExtractBell) return;
  r.bellBy = pid;
  r.pressureT = secs(2);
  setPhase(g, RunPhase.ExtractHoldout, secs(HOLDOUT_SECS));
  const p = g.players.get(pid);
  g.emit(Ev.Notice, [], `${p ? p.name.toUpperCase() : 'SOMEBODY'} RANG THE BELL. THE TRAIN IS 30 SECONDS OUT. HOLD THE PLATFORM.`);
  summonLeviathan(g);
}

/** Continuous spawn pressure during the getaway: a trickle near the posse, heavier on the islet. */
function pressure(g: Game, onIslet: boolean) {
  const r = g.run;
  if (--r.pressureT > 0) return;
  r.pressureT = secs(onIslet ? 3.2 : 6);
  const cap = pursuerCap(g, onIslet ? 12 : 8);
  if (livingRobots(g) >= cap) return;
  const rng = new Rng(hash32(g.seed, g.tick, 0x9e55));
  const isl = g.world.extractIslet;
  const count = onIslet ? rng.int(1, 2) : 2;
  const goldPulse = ++r.pulses % 3 === 0; // every third pulse leads with a gold bounty robot (CHASE.md)
  for (let i = 0; i < count; i++) {
    const t = rng.pick(onIslet ? [EType.Swarmer, EType.Swarmer, EType.Shooter, EType.Bomber] : [EType.Swarmer, EType.Shooter, EType.Shooter]);
    let x: number, z: number;
    if (onIslet) {
      const a = rng.range(0, Math.PI * 2), rr = isl.radius * rng.range(0.45, 0.7);
      x = isl.cx + Math.cos(a) * rr; z = isl.cz + Math.sin(a) * rr;
    } else {
      const c = posseCenter(g);
      const pts = clearSpawns(g).sort((a, b) => len2(a.x - c.x, a.z - c.z) - len2(b.x - c.x, b.z - c.z));
      const sp = pts[rng.int(0, Math.min(2, pts.length - 1))];
      x = sp.x + rng.range(-3, 3); z = sp.z + rng.range(-3, 3);
    }
    if (g.world.terrain(x, z) === NO_GROUND) continue;
    g.emit(Ev.SpawnWarn, [x, g.world.support(x, z, 60), z, t, secs(1.2)]);
    const e = g.spawnEnemy(t, x, z);
    if (goldPulse && i === 0) { e.gold = 1; e.hp *= GOLD_HP_MUL; e.maxHp *= GOLD_HP_MUL; g.emit(Ev.Notice, [], 'A GOLD BOUNTY: BRING IT DOWN FOR A TICKET'); }
  }
}
/** a gold bounty robot fell: a golden ticket; every TICKETS_PER_DRAFT pay a wildcard draft on the spot */
export function goldKilled(g: Game, by: number) {
  const r = g.run;
  r.tickets++;
  const p = by >= 0 ? g.players.get(by) : undefined;
  g.emit(Ev.Notice, [], `GOLDEN TICKET ${r.tickets}${p ? ' · ' + p.name.toUpperCase() : ''}`);
  if (r.tickets % TICKETS_PER_DRAFT === 0) g.offerDrafts(DraftKind.Uncommon, 0x601d + r.tickets);
}

/** Is this cowboy aboard the train (lifted out of the fight)? */
export const isAboard = (g: Game, pid: number) => (g.run.boardMask & (1 << (pid & 31))) !== 0;

function board(g: Game) {
  const r = g.run;
  const bp = boardPos(g.world);
  let waiting = 0;
  for (const p of g.players.values()) {
    if (isAboard(g, p.id)) continue;
    if (p.phase !== Phase.Alive || p.paused) continue;
    const d = Math.hypot(p.x - bp.x, p.y + 0.9 - bp.y, p.z - bp.z);
    if (d < BOARD_R) {
      r.boardMask |= 1 << (p.id & 31);
      g.emit(Ev.Notice, [], `${p.name.toUpperCase()} IS ABOARD.`);
      if (r.lastCall === 0) r.lastCall = secs(LAST_CALL_SECS);
      continue;
    }
    waiting++;
  }
  if (r.lastCall > 0) {
    r.lastCall--;
    r.timer = r.lastCall;
    if (waiting === 0 || r.lastCall === 0) {
      for (const e of g.enemies) e.target = -1;
      endRun(g, RunResult.Extracted);
      g.emit(Ev.Notice, [], 'EXTRACTED. G.U.L.P. THANKS YOU FOR YOUR SERVICE.');
    }
  }
}
